import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Schedule,
  BeachAccess,
  Event,
  AccessTime,
  LocationOn,
  Refresh,
  CheckCircle,
  Warning,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { getApiUrl } from '../utils/api';
import { formatCalendarDate } from '../utils/dates';

interface ScheduleData {
  id: number;
  customer_id: number;
  is_active: boolean;
  timezone: string;
  monday_time: string | null;
  tuesday_time: string | null;
  wednesday_time: string | null;
  thursday_time: string | null;
  friday_time: string | null;
  saturday_time: string | null;
  sunday_time: string | null;
  created_at: string;
  updated_at: string | null;
}

interface VacationPeriod {
  id: number;
  schedule_id: number;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  auto_resume: boolean;
  notify_caregiver: boolean;
  created_at: string;
  updated_at: string | null;
}

interface ScheduleException {
  id: number;
  schedule_id: number;
  date: string;
  action: 'ENABLE' | 'DISABLE' | 'MODIFY';
  new_time: string | null;
  reason: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

interface ScheduleSummary {
  customer_id: number;
  customer_name: string;
  is_active: boolean;
  timezone: string;
  next_check_in: string | null;
  is_on_vacation: boolean;
  active_vacation: VacationPeriod | null;
  scheduled_days: string[];
}

interface Customer {
  id: number;
  name: string;
  phone_number: string;
  caregiver_name: string;
  caregiver_phone: string;
  caregiver_email: string | null;
  is_active: boolean;
}

const ScheduleManagement: React.FC = () => {
  const apiUrl = getApiUrl();
  const { token } = useAuth();
  const { isAdmin, isEndUser } = useRole();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [scheduleData, setScheduleData] = useState<ScheduleData | null>(null);
  const [vacationPeriods, setVacationPeriods] = useState<VacationPeriod[]>([]);
  const [scheduleExceptions, setScheduleExceptions] = useState<ScheduleException[]>([]);
  const [scheduleSummary, setScheduleSummary] = useState<ScheduleSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);

  // Form states
  const [scheduleForm, setScheduleForm] = useState({
    timezone: 'America/New_York',
    is_active: true,
    monday_time: '',
    tuesday_time: '',
    wednesday_time: '',
    thursday_time: '',
    friday_time: '',
    saturday_time: '',
    sunday_time: ''
  });

  const [vacationForm, setVacationForm] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    is_active: true,
    auto_resume: true,
    notify_caregiver: true
  });

  const [exceptionForm, setExceptionForm] = useState({
    date: '',
    action: 'DISABLE' as 'ENABLE' | 'DISABLE' | 'MODIFY',
    new_time: '',
    reason: ''
  });

  const formatApiError = (detail: unknown, fallback: string): string => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const first = detail[0] as any;
      if (first?.msg && typeof first.msg === 'string') return first.msg;
      return detail
        .map((item: any) => (typeof item?.msg === 'string' ? item.msg : null))
        .filter(Boolean)
        .join(', ') || fallback;
    }
    if (typeof detail === 'object' && (detail as any).msg) return String((detail as any).msg);
    return fallback;
  };

  // Load customers on component mount
  useEffect(() => {
    if (isAdmin) {
      loadCustomers();
    } else if (isEndUser) {
      loadAssignedCustomers();
    }
  }, [isAdmin, isEndUser]);

  // Load schedule data when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      loadScheduleData();
      loadScheduleSummary();
    }
  }, [selectedCustomer]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/customers/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      } else {
        setError('Failed to load customers');
      }
    } catch (err) {
      setError('Error loading customers');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedCustomers = async () => {
    try {
      setLoading(true);
      console.log('ScheduleManagement: Loading assigned customers with token:', token ? 'exists' : 'null');
      console.log('ScheduleManagement: API URL:', apiUrl);
      
      const response = await fetch(`${apiUrl}/customers/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('ScheduleManagement: Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('ScheduleManagement: Customers data received:', data);
        setCustomers(data);
        // Auto-select the first assigned customer for end users
        if (data.length > 0) {
          setSelectedCustomer(data[0].id);
        }
      } else {
        const errorText = await response.text();
        console.error('ScheduleManagement: API error:', response.status, errorText);
        setError('Failed to load assigned customers');
      }
    } catch (err) {
      console.error('ScheduleManagement: Network error:', err);
      setError('Error loading assigned customers');
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleData = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setScheduleData(data);
        // Update the form with the actual schedule data
        setScheduleForm({
          timezone: data.timezone || 'America/New_York',
          is_active: data.is_active,
          monday_time: data.monday_time || '',
          tuesday_time: data.tuesday_time || '',
          wednesday_time: data.wednesday_time || '',
          thursday_time: data.thursday_time || '',
          friday_time: data.friday_time || '',
          saturday_time: data.saturday_time || '',
          sunday_time: data.sunday_time || ''
        });
        loadVacationPeriods();
        loadScheduleExceptions();
      } else if (response.status === 404) {
        setScheduleData(null);
        setVacationPeriods([]);
        setScheduleExceptions([]);
      } else {
        setError('Failed to load schedule data');
      }
    } catch (err) {
      setError('Error loading schedule data');
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleSummary = async () => {
    if (!selectedCustomer) return;

    try {
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setScheduleSummary(data);
      }
    } catch (err) {
      console.error('Error loading schedule summary:', err);
    }
  };

  const loadVacationPeriods = async () => {
    if (!selectedCustomer) return;

    try {
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}/vacations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVacationPeriods(data);
      }
    } catch (err) {
      console.error('Error loading vacation periods:', err);
    }
  };

  const loadScheduleExceptions = async () => {
    if (!selectedCustomer) return;

    try {
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}/exceptions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setScheduleExceptions(data);
      }
    } catch (err) {
      console.error('Error loading schedule exceptions:', err);
    }
  };

  const createSampleSchedule = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}/test/create-sample-schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setSuccess('Sample schedule created successfully');
        loadScheduleData();
        loadScheduleSummary();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create sample schedule');
      }
    } catch (err) {
      setError('Error creating sample schedule');
    } finally {
      setLoading(false);
    }
  };

  const createSampleVacation = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}/test/create-sample-vacation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setSuccess('Sample vacation period created successfully');
        loadVacationPeriods();
        loadScheduleSummary();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create sample vacation');
      }
    } catch (err) {
      setError('Error creating sample vacation');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleSubmit = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      
      // Prepare schedule data, converting empty strings to null for time fields
      const scheduleData = {
        customer_id: selectedCustomer,
        timezone: scheduleForm.timezone,
        is_active: scheduleForm.is_active,
        monday_time: scheduleForm.monday_time || null,
        tuesday_time: scheduleForm.tuesday_time || null,
        wednesday_time: scheduleForm.wednesday_time || null,
        thursday_time: scheduleForm.thursday_time || null,
        friday_time: scheduleForm.friday_time || null,
        saturday_time: scheduleForm.saturday_time || null,
        sunday_time: scheduleForm.sunday_time || null
      };
      
      const response = await fetch(`${apiUrl}/schedules/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scheduleData)
      });
      
      if (response.ok) {
        setSuccess('Schedule created successfully');
        setScheduleDialogOpen(false);
        loadScheduleData();
        loadScheduleSummary();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create schedule');
      }
    } catch (err) {
      setError('Error creating schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleVacationSubmit = async () => {
    if (!selectedCustomer) return;

    try {
      if (!vacationForm.name.trim()) {
        setError('Vacation name is required');
        return;
      }
      if (!vacationForm.start_date || !vacationForm.end_date) {
        setError('Start date and end date are required');
        return;
      }
      if (new Date(vacationForm.end_date) < new Date(vacationForm.start_date)) {
        setError('End date must be on or after start date');
        return;
      }

      setLoading(true);
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}/vacations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(vacationForm)
      });
      
      if (response.ok) {
        const createdVacation = await response.json();
        // Update immediately so users see the new vacation without waiting for refresh.
        setVacationPeriods((prev) => [...prev, createdVacation]);
        setSuccess('Vacation period created successfully');
        setVacationDialogOpen(false);
        setVacationForm({
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          is_active: true,
          auto_resume: true,
          notify_caregiver: true
        });
        await Promise.all([loadVacationPeriods(), loadScheduleSummary(), loadScheduleData()]);
      } else {
        let errorMessage = 'Failed to create vacation period';
        try {
          const errorData = await response.json();
          errorMessage = formatApiError(errorData?.detail, errorMessage);
        } catch {
          // Keep fallback message when response is not JSON
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError('Error creating vacation period');
    } finally {
      setLoading(false);
    }
  };

  const handleExceptionSubmit = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/schedules/${selectedCustomer}/exceptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exceptionForm)
      });
      
      if (response.ok) {
        setSuccess('Schedule exception created successfully');
        setExceptionDialogOpen(false);
        loadScheduleExceptions();
        loadScheduleSummary();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create schedule exception');
      }
    } catch (err) {
      setError('Error creating schedule exception');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return 'Not set';
    return timeStr;
  };

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? <CheckCircle color="success" /> : <ErrorIcon color="error" />;
  };

  const getVacationStatusIcon = (isOnVacation: boolean) => {
    return isOnVacation ? <BeachAccess color="warning" /> : <CheckCircle color="success" />;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Schedule Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Customer Selection - Only show for admins */}
      {isAdmin && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Customer
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Customer</InputLabel>
              <Select
                value={selectedCustomer || ''}
                onChange={(e) => setSelectedCustomer(e.target.value as number)}
                label="Customer"
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name} ({customer.phone_number})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Show current customer for end users */}
      {isEndUser && selectedCustomer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              My Loved One's Schedule
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {customers.find(c => c.id === selectedCustomer)?.name}
            </Typography>
            
            {/* Check-in Times Display */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Daily Check-in Times
              </Typography>
              <Grid container spacing={2}>
                {scheduleData && scheduleData.monday_time && (
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main">
                        {new Date(`2000-01-01T${scheduleData.monday_time}`).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Morning Check-in
                      </Typography>
                    </Card>
                  </Grid>
                )}
                {scheduleData && scheduleData.tuesday_time && scheduleData.tuesday_time !== scheduleData.monday_time && (
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main">
                        {new Date(`2000-01-01T${scheduleData.tuesday_time}`).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Evening Check-in
                      </Typography>
                    </Card>
                  </Grid>
                )}
                {(!scheduleData || !scheduleData.monday_time) && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        No check-in times scheduled
                      </Typography>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Timezone and Schedule Modification */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Schedule Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={scheduleForm.timezone}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, timezone: e.target.value })}
                      label="Timezone"
                    >
                      <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                      <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                      <MenuItem value="America/Phoenix">Arizona Time (MST)</MenuItem>
                      <MenuItem value="America/Anchorage">Alaska Time (AKST)</MenuItem>
                      <MenuItem value="Pacific/Honolulu">Hawaii Time (HST)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={scheduleForm.is_active}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.checked })}
                      />
                    }
                    label="Schedule Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleScheduleSubmit}
                    disabled={loading}
                    sx={{ mr: 2 }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Update Schedule'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      loadScheduleData();
                      loadScheduleSummary();
                    }}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {selectedCustomer && (
        <>
          {/* Schedule Summary */}
          {scheduleSummary && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">
                    Schedule Summary - {scheduleSummary.customer_name}
                  </Typography>
                  <Box>
                    {getStatusIcon(scheduleSummary.is_active)}
                    {getVacationStatusIcon(scheduleSummary.is_on_vacation)}
                  </Box>
                </Box>
                
                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">
                      Timezone: {scheduleSummary.timezone}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Next Check-in: {scheduleSummary.next_check_in ? new Date(scheduleSummary.next_check_in).toLocaleString() : 'None scheduled'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Vacation Status: {scheduleSummary.is_on_vacation ? 'On Vacation (check-ins paused)' : 'Not on vacation'}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">
                      Scheduled Days: {scheduleSummary.scheduled_days.join(', ') || 'None'}
                    </Typography>
                    {scheduleSummary.active_vacation && (
                      <Typography variant="body2" color="textSecondary">
                        Active Vacation: {scheduleSummary.active_vacation.name} ({formatCalendarDate(scheduleSummary.active_vacation.start_date)} - {formatCalendarDate(scheduleSummary.active_vacation.end_date)})
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setScheduleDialogOpen(true)}
                  disabled={!!scheduleData}
                >
                  Create Schedule
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<BeachAccess />}
                  onClick={() => setVacationDialogOpen(true)}
                  disabled={!scheduleData}
                >
                  Add Vacation
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Event />}
                  onClick={() => setExceptionDialogOpen(true)}
                  disabled={!scheduleData}
                >
                  Add Exception
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Schedule />}
                  onClick={createSampleSchedule}
                  disabled={!!scheduleData}
                >
                  Create Sample Schedule
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<BeachAccess />}
                  onClick={createSampleVacation}
                  disabled={!scheduleData}
                >
                  Create Sample Vacation
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => {
                    loadScheduleData();
                    loadScheduleSummary();
                  }}
                >
                  Refresh
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Schedule Details */}
          {scheduleData && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Schedule Details
                </Typography>
                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">
                      Status: {scheduleData.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Timezone: {scheduleData.timezone}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="body2" color="textSecondary">
                      Created: {new Date(scheduleData.created_at).toLocaleString()}
                    </Typography>
                    {scheduleData.updated_at && (
                      <Typography variant="body2" color="textSecondary">
                        Updated: {new Date(scheduleData.updated_at).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Daily Schedule
                </Typography>
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={2}>
                  {[
                    { day: 'Monday', time: scheduleData.monday_time },
                    { day: 'Tuesday', time: scheduleData.tuesday_time },
                    { day: 'Wednesday', time: scheduleData.wednesday_time },
                    { day: 'Thursday', time: scheduleData.thursday_time },
                    { day: 'Friday', time: scheduleData.friday_time },
                    { day: 'Saturday', time: scheduleData.saturday_time },
                    { day: 'Sunday', time: scheduleData.sunday_time }
                  ].map(({ day, time }) => (
                    <Box key={day}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTime fontSize="small" />
                        <Typography variant="body2">
                          {day}: {formatTime(time)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Vacation Periods */}
          {vacationPeriods.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Vacation Periods
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell>Auto Resume</TableCell>
                        <TableCell>Notify Caregiver</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vacationPeriods.map((vacation) => (
                        <TableRow key={vacation.id}>
                          <TableCell>{vacation.name}</TableCell>
                          <TableCell>{formatCalendarDate(vacation.start_date)}</TableCell>
                          <TableCell>{formatCalendarDate(vacation.end_date)}</TableCell>
                          <TableCell>
                            <Chip
                              label={vacation.is_active ? 'Yes' : 'No'}
                              color={vacation.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={vacation.auto_resume ? 'Yes' : 'No'}
                              color={vacation.auto_resume ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={vacation.notify_caregiver ? 'Yes' : 'No'}
                              color={vacation.notify_caregiver ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small">
                              <Edit />
                            </IconButton>
                            <IconButton size="small" color="error">
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Schedule Exceptions */}
          {scheduleExceptions.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Schedule Exceptions
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>New Time</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduleExceptions.map((exception) => (
                        <TableRow key={exception.id}>
                          <TableCell>{formatCalendarDate(exception.date)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={exception.action} 
                              color={exception.action === 'DISABLE' ? 'error' : exception.action === 'MODIFY' ? 'warning' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatTime(exception.new_time)}</TableCell>
                          <TableCell>{exception.reason || '-'}</TableCell>
                          <TableCell>
                            {getStatusIcon(exception.is_active)}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small">
                              <Edit />
                            </IconButton>
                            <IconButton size="small" color="error">
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {/* Schedule Creation Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Schedule</DialogTitle>
        <DialogContent>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={2} sx={{ mt: 1 }}>
            <Box>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={scheduleForm.timezone}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, timezone: e.target.value })}
                  label="Timezone"
                >
                  <MenuItem value="America/New_York">America/New_York</MenuItem>
                  <MenuItem value="America/Chicago">America/Chicago</MenuItem>
                  <MenuItem value="America/Denver">America/Denver</MenuItem>
                  <MenuItem value="America/Los_Angeles">America/Los_Angeles</MenuItem>
                  <MenuItem value="UTC">UTC</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={scheduleForm.is_active}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
              <Box key={day}>
                <TextField
                  fullWidth
                  label={`${day.charAt(0).toUpperCase() + day.slice(1)} Time`}
                  type="time"
                  value={scheduleForm[day as keyof typeof scheduleForm]}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, [day]: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleScheduleSubmit} variant="contained" disabled={loading}>
            Create Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vacation Creation Dialog */}
      <Dialog open={vacationDialogOpen} onClose={() => setVacationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Vacation Period</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Box>
              <TextField
                fullWidth
                label="Vacation Name"
                value={vacationForm.name}
                onChange={(e) => setVacationForm({ ...vacationForm, name: e.target.value })}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={vacationForm.description}
                onChange={(e) => setVacationForm({ ...vacationForm, description: e.target.value })}
              />
            </Box>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={vacationForm.start_date}
                  onChange={(e) => setVacationForm({ ...vacationForm, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={vacationForm.end_date}
                  onChange={(e) => setVacationForm({ ...vacationForm, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={vacationForm.is_active}
                    onChange={(e) => setVacationForm({ ...vacationForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={vacationForm.auto_resume}
                    onChange={(e) => setVacationForm({ ...vacationForm, auto_resume: e.target.checked })}
                  />
                }
                label="Auto Resume"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={vacationForm.notify_caregiver}
                    onChange={(e) => setVacationForm({ ...vacationForm, notify_caregiver: e.target.checked })}
                  />
                }
                label="Notify Caregiver"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVacationDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleVacationSubmit} variant="contained" disabled={loading}>
            Add Vacation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exception Creation Dialog */}
      <Dialog open={exceptionDialogOpen} onClose={() => setExceptionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Schedule Exception</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Box>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={exceptionForm.date}
                onChange={(e) => setExceptionForm({ ...exceptionForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={exceptionForm.action}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, action: e.target.value as 'ENABLE' | 'DISABLE' | 'MODIFY' })}
                  label="Action"
                >
                  <MenuItem value="DISABLE">Disable Check-in</MenuItem>
                  <MenuItem value="ENABLE">Enable Check-in</MenuItem>
                  <MenuItem value="MODIFY">Modify Time</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {exceptionForm.action === 'MODIFY' && (
              <Box>
                <TextField
                  fullWidth
                  label="New Time"
                  type="time"
                  value={exceptionForm.new_time}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, new_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
            <Box>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={exceptionForm.reason}
                onChange={(e) => setExceptionForm({ ...exceptionForm, reason: e.target.value })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExceptionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExceptionSubmit} variant="contained" disabled={loading}>
            Add Exception
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScheduleManagement;
